import React, { useMemo, useRef, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useProgressiveAuth } from '@/hooks/auth/useProgressiveAuth';
import { UnifiedLayoutSkeleton } from '@/components/layout/UnifiedLayoutSkeleton';
import { navigationDebugger } from '@/utils/navigationDebugger';
import { storePendingAction } from '@/utils/pendingActionStorage';
import { resolveActionFromParams } from '@/utils/actionTypeResolver';

interface PreRouteGateProps {
  children: React.ReactNode;
}

/**
 * Centralized route gate that handles ALL authentication, permissions, and routing
 * decisions BEFORE any component renders. This prevents double loading and visible redirects.
 * 
 * Public Routes (accessible without authentication):
 * - /c/:companyShortCode - Public company job board (short URL)
 * - /c/:companyShortCode/:jobShortCode - Public job detail page (short URL)
 * - /company/:uuid - Legacy company job board (backward compatibility)
 * - /company/:uuid/:jobUuid - Legacy job detail page (backward compatibility)
 * - /company-listings - Public company directory
 * - /privacy-policy, /terms-of-service - Legal pages
 * - /reset-password - Password recovery
 */
export const PreRouteGate: React.FC<PreRouteGateProps> = ({ children }) => {
  const location = useLocation();
  const { 
    user, 
    isReady, 
    isFullyLoaded,
    hasInternalAccess, 
    hasCompanyAccess, 
    hasRecruiterAccess, 
    hasTalentAccess,
    hasNannyAccess,
    hasMotherAccess
  } = useProgressiveAuth();

  // Track if we've loaded once - prevents skeleton from showing on background refreshes
  const hasLoadedOnceRef = useRef(false);
  
  useEffect(() => {
    if (isFullyLoaded && isReady) {
      hasLoadedOnceRef.current = true;
    }
  }, [isFullyLoaded, isReady]);

  // Cache for route decisions to prevent unnecessary recalculations
  const routeDecision = useMemo(() => {
    const currentPath = location.pathname;

    // Public routes - accessible to all users (authenticated or not)
    if (currentPath === '/company-listings' || 
        currentPath.startsWith('/company/') ||
        currentPath.startsWith('/c/') ||  // Short URL format for public job boards
        currentPath.startsWith('/demo') ||  // Demo interview pages
        currentPath === '/privacy-policy' ||
        currentPath === '/terms-of-service' ||
        currentPath === '/nanny-platform-auth' ||  // Nanny platform public auth
        currentPath === '/nanny' ||                // Smart nanny redirect
        currentPath === '/mother' ||               // Smart mother redirect
        currentPath === '/nanny-auth' ||           // Legacy nanny auth redirect
        currentPath === '/mother-auth') {          // Legacy mother auth redirect
      return { action: 'allow', path: currentPath };
    }

    // Always allow reset-password page - it handles its own session validation
    if (currentPath === '/reset-password') {
      return { action: 'allow', path: currentPath };
    }

    // Wait for auth system to be fully ready (including all roles)
    // But only show skeleton on first load - not on background refreshes
    if (!isReady || (!hasLoadedOnceRef.current && !isFullyLoaded)) {
      return { action: 'loading' };
    }

    // Index route redirect
    if (currentPath === '/') {
      if (!user) {
        navigationDebugger.logRedirect('/', '/auth', 'PreRouteGate (no user)');
        return { action: 'redirect', path: '/auth' };
      }

      // If name collection is required, route to /auth first
      const forceNameCollectionIndex = localStorage.getItem('force_name_collection') === 'true';
      if (forceNameCollectionIndex) {
        navigationDebugger.logRedirect('/', '/auth', 'PreRouteGate (force name collection)');
        return { action: 'redirect', path: '/auth' };
      }
      
      const workspace = getDefaultWorkspace({ hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess }, currentPath);
      navigationDebugger.logRedirect('/', workspace, 'PreRouteGate (authenticated)');
      return { action: 'redirect', path: workspace };
    }

    // Auth route handling
    if (currentPath === '/auth') {
      // Check if this is a recovery flow before redirecting authenticated users
      const hasRecoveryParams = location.search.includes('type=recovery') || 
                               location.search.includes('code=') || 
                               location.search.includes('token=') ||
                               location.hash.includes('access_token');
      
      // Check if name collection is forced (new signup flow)
      const forceNameCollection = localStorage.getItem('force_name_collection') === 'true';
      
      if (user && !hasRecoveryParams && !forceNameCollection) {
        const workspace = getDefaultWorkspace({ hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess }, currentPath);
        navigationDebugger.logRedirect('/auth', workspace, 'PreRouteGate (already authenticated)');
        return { action: 'redirect', path: workspace };
      }
      return { action: 'allow', path: currentPath };
    }

    // Protected routes require authentication
    if (!user) {
      // Check if current URL has action parameters that should be preserved
      const searchParams = new URLSearchParams(location.search);
      const applicationId = searchParams.get('applicationId');
      const stageId = searchParams.get('stage');
      const action = searchParams.get('action');
      const stageType = searchParams.get('stageType');
      const source = searchParams.get('source') as 'email' | 'notification' | undefined;
      
      // Store pending action if we have actionable URL params
      if (applicationId && (action || stageId || stageType)) {
        const { actionType } = resolveActionFromParams({ action, stageType });
        
        storePendingAction({
          type: actionType,
          applicationId,
          stageId: stageId || undefined,
          stageType: stageType || undefined,
          returnPath: currentPath + location.search,
          source: source || 'direct'
        });
        
        console.log('[PreRouteGate] Stored pending action before auth redirect');
      }
      
      navigationDebugger.logRedirect(currentPath, '/auth', 'PreRouteGate (unauthenticated)');
      return { action: 'redirect', path: '/auth' };
    }

    // If name collection is required, force redirect to /auth
    const forceNameCollectionGlobal = localStorage.getItem('force_name_collection') === 'true';
    if (forceNameCollectionGlobal && currentPath !== '/auth') {
      navigationDebugger.logRedirect(currentPath, '/auth', 'PreRouteGate (force name collection)');
      return { action: 'redirect', path: '/auth' };
    }

    // Workspace permission checks
    if (currentPath.includes('/workspace/')) {
      if (currentPath.includes('/workspace/internal/') && !hasInternalAccess) {
        const workspace = getDefaultWorkspace({ hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess }, currentPath);
        navigationDebugger.logRedirect(currentPath, workspace, 'PreRouteGate (no internal access)');
        return { action: 'redirect', path: workspace };
      }
      
      if (currentPath.includes('/workspace/company/') && !hasCompanyAccess) {
        const workspace = getDefaultWorkspace({ hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess }, currentPath);
        navigationDebugger.logRedirect(currentPath, workspace, 'PreRouteGate (no company access)');
        return { action: 'redirect', path: workspace };
      }
      
      if (currentPath.includes('/workspace/recruiter/') && !hasRecruiterAccess) {
        const workspace = getDefaultWorkspace({ hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess }, currentPath);
        navigationDebugger.logRedirect(currentPath, workspace, 'PreRouteGate (no recruiter access)');
        return { action: 'redirect', path: workspace };
      }
      
      if (currentPath.includes('/workspace/talent/') && !hasTalentAccess) {
        const workspace = getDefaultWorkspace({ hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess }, currentPath);
        navigationDebugger.logRedirect(currentPath, workspace, 'PreRouteGate (no talent access)');
        return { action: 'redirect', path: workspace };
      }
      
      if (currentPath.includes('/workspace/nanny/') && !hasNannyAccess) {
        navigationDebugger.logRedirect(currentPath, '/nanny-platform-auth', 'PreRouteGate (no nanny access - guide to role creation)');
        return { action: 'redirect', path: '/nanny-platform-auth' };
      }
      
      if (currentPath.includes('/workspace/mother/') && !hasMotherAccess) {
        navigationDebugger.logRedirect(currentPath, '/nanny-platform-auth', 'PreRouteGate (no mother access - guide to role creation)');
        return { action: 'redirect', path: '/nanny-platform-auth' };
      }
    }

    return { action: 'allow', path: currentPath };
  }, [location.pathname, user, isReady, isFullyLoaded, hasInternalAccess, hasCompanyAccess, hasRecruiterAccess, hasTalentAccess, hasNannyAccess, hasMotherAccess]);

  // Handle loading state
  if (routeDecision.action === 'loading') {
    return <UnifiedLayoutSkeleton />;
  }

  // Handle redirects
  if (routeDecision.action === 'redirect') {
    return <Navigate to={routeDecision.path} replace />;
  }

  // Allow the route to render
  return <>{children}</>;
};

/**
 * Get the default workspace based on user access flags and preferences
 */
function getDefaultWorkspace(access: {
  hasInternalAccess: boolean;
  hasCompanyAccess: boolean;
  hasRecruiterAccess: boolean;
  hasTalentAccess: boolean;
  hasNannyAccess: boolean;
  hasMotherAccess: boolean;
}, currentPath?: string) {
  // First, try to infer workspace from current URL if provided
  if (currentPath) {
    if (currentPath.includes('/workspace/nanny/') && access.hasNannyAccess) {
      return '/workspace/nanny/dashboard';
    }
    if (currentPath.includes('/workspace/mother/') && access.hasMotherAccess) {
      return '/workspace/mother/dashboard';
    }
    if (currentPath.includes('/workspace/talent/') && access.hasTalentAccess) {
      return '/workspace/talent/jobs';
    }
    if (currentPath.includes('/workspace/company/') && access.hasCompanyAccess) {
      return '/workspace/company/hiring-center';
    }
    if (currentPath.includes('/workspace/recruiter/') && access.hasRecruiterAccess) {
      return '/workspace/recruiter/talent-search';
    }
    if (currentPath.includes('/workspace/internal/') && access.hasInternalAccess) {
      return '/workspace/internal/dashboard';
    }
  }
  
  // Second, check stored workspace preference
  const storedMode = localStorage.getItem('workspace_mode');
  if (storedMode === 'nanny' && access.hasNannyAccess) {
    return '/workspace/nanny/dashboard';
  }
  if (storedMode === 'mother' && access.hasMotherAccess) {
    return '/workspace/mother/dashboard';
  }
  if (storedMode === 'talent' && access.hasTalentAccess) {
    return '/workspace/talent/jobs';
  }
  if (storedMode === 'company' && access.hasCompanyAccess) {
    return '/workspace/company/hiring-center';
  }
  if (storedMode === 'recruiter' && access.hasRecruiterAccess) {
    return '/workspace/recruiter/talent-search';
  }
  if (storedMode === 'internal' && access.hasInternalAccess) {
    return '/workspace/internal/dashboard';
  }
  
  // Finally, fall back to priority order (nanny/mother workspaces first as separate platform)
  if (access.hasNannyAccess) {
    return '/workspace/nanny/dashboard';
  }
  if (access.hasMotherAccess) {
    return '/workspace/mother/dashboard';
  }
  if (access.hasTalentAccess) {
    return '/workspace/talent/jobs';
  }
  if (access.hasCompanyAccess) {
    return '/workspace/company/hiring-center';
  }
  if (access.hasRecruiterAccess) {
    return '/workspace/recruiter/talent-search';
  }
  if (access.hasInternalAccess) {
    return '/workspace/internal/dashboard';
  }
  
  // No access to any workspace
  return '/evee-ai';
}